import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HorizontalBar } from "react-chartjs-2";
import Typography from "@material-ui/core/Typography";
import {
  AnalyseIncidentClass,
  AnalyseIncidentInterval,
} from "store/analyse-store/analyseStore";
import { hexToRgba } from "pages/labelerVideo/utils/hexToRgba";
import IncidentsChartHeader from "./IncidentsChartHeader";
import IncidentsChartStripeOverlay from "./IncidentsChartStripeOverlay";
import {
  collectIncidentsChartStripeOverlays,
  INCIDENTS_CHART_BAR_PREFIX,
  StripeOverlayRect,
} from "./incidentsChartStripes";
import {
  INCIDENTS_CHART_GROUPS_FLAG,
  INCIDENTS_CHART_ROWS_PER_CLASS,
} from "./incidentsChartGroupOverlay";
import "./incidentsChartGroupPlugin";

const ROW_HEIGHT = 44;
const MIN_HEIGHT = 240;
const TRANSPARENT = "rgba(0,0,0,0)";

const PAD_PREFIX = "__pad__";
const BAR_PREFIX = INCIDENTS_CHART_BAR_PREFIX;

const formatTimeLabel = (seconds: number) => {
  const safe = Math.max(0, seconds);
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

type RowSource = "labeler" | "primaryModel" | "secondaryModel";

type Props = {
  classes: AnalyseIncidentClass[];
  labelerIntervals: AnalyseIncidentInterval[];
  modelIntervals: AnalyseIncidentInterval[];
  secondaryModelIntervals?: AnalyseIncidentInterval[];
  durationSec: number;
  durationExactSec: number;
  modelLabel: string | null;
  secondaryModelLabel?: string | null;
  selectedModelCount?: number;
  mediaName: string;
  isLoadingVideos?: boolean;
};

type RowMeta = {
  label: string;
  classColor: string;
  source: RowSource;
  sourceLabel: string;
  intervals: AnalyseIncidentInterval[];
};

const rowBackground = (row: RowMeta) => {
  if (row.source === "labeler") {
    return hexToRgba(row.classColor, 0.85);
  }
  // Model predictions are painted by the HTML stripe overlay.
  return TRANSPARENT;
};

const IncidentsChart: React.FC<Props> = ({
  classes,
  labelerIntervals,
  modelIntervals,
  secondaryModelIntervals = [],
  durationSec,
  durationExactSec,
  modelLabel,
  secondaryModelLabel = null,
  selectedModelCount,
  mediaName,
  isLoadingVideos = false,
}) => {
  const chartRef = useRef<any>(null);
  const [stripeOverlays, setStripeOverlays] = useState<StripeOverlayRect[]>(
    []
  );

  const syncStripeOverlays = useCallback(() => {
    const chart = chartRef.current?.chartInstance;
    if (!chart) {
      setStripeOverlays([]);
      return;
    }
    setStripeOverlays(collectIncidentsChartStripeOverlays(chart));
  }, []);

  const hasPrimaryModel = Boolean(modelLabel);
  const hasSecondaryModel = Boolean(secondaryModelLabel);
  const rowsPerClass =
    1 + (hasPrimaryModel ? 1 : 0) + (hasSecondaryModel ? 1 : 0);
  const resolvedModelCount =
    selectedModelCount != null
      ? selectedModelCount
      : (hasPrimaryModel ? 1 : 0) + (hasSecondaryModel ? 1 : 0);
  const labelRowCount = classes.length;

  const { rows, maxIntervals } = useMemo(() => {
    const sortByStart = (a: AnalyseIncidentInterval, b: AnalyseIncidentInterval) =>
      a.startTime - b.startTime;

    const built: RowMeta[] = [];
    classes.forEach((cls) => {
      built.push({
        label: `${cls.name}`,
        classColor: cls.color,
        source: "labeler",
        sourceLabel: "Labeler",
        intervals: labelerIntervals
          .filter((i) => i.className === cls.name)
          .sort(sortByStart),
      });
      if (hasPrimaryModel && modelLabel) {
        built.push({
          label: `${modelLabel}`,
          classColor: cls.color,
          source: "primaryModel",
          sourceLabel: modelLabel,
          intervals: modelIntervals
            .filter((i) => i.className === cls.name)
            .sort(sortByStart),
        });
      }
      if (hasSecondaryModel && secondaryModelLabel) {
        built.push({
          label: `${secondaryModelLabel}`,
          classColor: cls.color,
          source: "secondaryModel",
          sourceLabel: secondaryModelLabel,
          intervals: secondaryModelIntervals
            .filter((i) => i.className === cls.name)
            .sort(sortByStart),
        });
      }
    });

    const max = built.reduce(
      (acc, row) => Math.max(acc, row.intervals.length),
      0
    );
    return { rows: built, maxIntervals: max };
  }, [
    classes,
    labelerIntervals,
    modelIntervals,
    secondaryModelIntervals,
    modelLabel,
    secondaryModelLabel,
    hasPrimaryModel,
    hasSecondaryModel,
  ]);

  const data = useMemo(() => {
    const labels = rows.map((r) => r.label);
    const datasets: any[] = [];

    for (let segIdx = 0; segIdx < maxIntervals; segIdx += 1) {
      const padData = rows.map((row) => {
        if (segIdx >= row.intervals.length) return 0;
        const interval = row.intervals[segIdx];
        const prevEnd =
          segIdx === 0 ? 0 : row.intervals[segIdx - 1].endTime;
        return Math.max(0, interval.startTime - prevEnd);
      });
      datasets.push({
        label: `${PAD_PREFIX}${segIdx}`,
        data: padData,
        backgroundColor: TRANSPARENT,
        borderColor: TRANSPARENT,
        hoverBackgroundColor: TRANSPARENT,
        borderWidth: 0,
        stack: "gantt",
      });

      const barData = rows.map((row) => {
        if (segIdx >= row.intervals.length) return 0;
        const interval = row.intervals[segIdx];
        return Math.max(0, interval.endTime - interval.startTime);
      });
      const backgroundColor = rows.map((row) => rowBackground(row));
      const hoverBackgroundColor = rows.map((row) =>
        row.source === "labeler"
          ? hexToRgba(row.classColor, 0.95)
          : TRANSPARENT
      );
      const borderColor = rows.map((row) => row.classColor);

      datasets.push({
        label: `${BAR_PREFIX}${segIdx}`,
        data: barData,
        backgroundColor,
        hoverBackgroundColor,
        borderColor,
        borderWidth: 0,
        stack: "gantt",
        _intervals: rows.map((row) =>
          segIdx < row.intervals.length ? row.intervals[segIdx] : null
        ),
        _sourceLabels: rows.map((row) => row.sourceLabel),
        _rowSources: rows.map((row) => row.source),
        _rowColors: rows.map((row) => row.classColor),
      });
    }

    return { labels, datasets };
  }, [rows, maxIntervals]);

  const options = useMemo(
    () =>
      ({
        [INCIDENTS_CHART_GROUPS_FLAG]: true,
        [INCIDENTS_CHART_ROWS_PER_CLASS]: rowsPerClass,
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 250,
          onComplete: () => {
            syncStripeOverlays();
          },
        } as any,
        legend: { display: false },
        layout: { padding: { top: 4, right: 12, bottom: 4, left: 4 } },
        tooltips: {
          filter: (item: any, dataAll: any) => {
            const ds = dataAll.datasets[item.datasetIndex];
            return (
              ds &&
              typeof ds.label === "string" &&
              ds.label.indexOf(BAR_PREFIX) === 0 &&
              Number(item.xLabel) > 0
            );
          },
          callbacks: {
            title: (items: any[], dataAll: any) => {
              if (!items.length) return "";
              const item = items[0];
              const ds = dataAll.datasets[item.datasetIndex];
              const interval = ds._intervals
                ? ds._intervals[item.index]
                : null;
              const source = ds._sourceLabels
                ? ds._sourceLabels[item.index]
                : "";
              if (!interval) return "";
              return `${interval.className} - ${source}`;
            },
            label: (item: any, dataAll: any) => {
              const ds = dataAll.datasets[item.datasetIndex];
              const interval = ds._intervals
                ? ds._intervals[item.index]
                : null;
              if (!interval) return "";
              const start = formatTimeLabel(interval.startTime);
              const end = formatTimeLabel(interval.endTime);
              const dur = Math.max(
                0,
                interval.endTime - interval.startTime
              ).toFixed(1);
              return `de ${start} a ${end}  (${dur}s)`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              ticks: {
                min: 0,
                max: Math.max(durationSec, 1),
                stepSize: Math.max(1, Math.round(durationSec / 12)),
                callback: (value: number) => formatTimeLabel(Number(value)),
                fontColor: "#666",
              },
              gridLines: { color: "#eee" },
              scaleLabel: {
                display: true,
                labelString: "Temps (mm:ss)",
                fontColor: "#888",
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: {
                autoSkip: false,
                fontColor: "#444",
                fontSize: 11,
              },
              gridLines: { color: "#f4f4f4" },
            },
          ],
        },
      } as any),
    [durationSec, rowsPerClass, syncStripeOverlays]
  );

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      syncStripeOverlays();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [data, syncStripeOverlays]);

  useEffect(() => {
    const onResize = () => syncStripeOverlays();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [syncStripeOverlays]);

  if (rows.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Typography variant="body2" color="textSecondary">
          Aucune classe d&apos;incident disponible pour la video selectionnee.
        </Typography>
      </div>
    );
  }

  const chartHeight = Math.max(MIN_HEIGHT, rows.length * ROW_HEIGHT + 70);

  return (
    <>
      <IncidentsChartHeader
        mediaName={mediaName}
        durationExactSec={durationExactSec}
        labelRowCount={labelRowCount}
        selectedModelCount={resolvedModelCount}
        modelLabel={modelLabel}
        secondaryModelLabel={secondaryModelLabel}
        isLoadingVideos={isLoadingVideos}
      />

      <div style={{ position: "relative", height: chartHeight, width: "100%" }}>
        <HorizontalBar ref={chartRef} data={data} options={options} />
        <IncidentsChartStripeOverlay overlays={stripeOverlays} />
      </div>
    </>
  );
};

export default IncidentsChart;
